import { Suspense, lazy, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import {
  CMS_SCREENS,
  CMS_SCREENS_BY_KEY,
  type CmsScreen,
  type CmsScreenProps,
} from '../cmsScreenRegistry';
import CmsPendingScreen from './CmsPendingScreen';

/**
 * ★★ MOUNT ONCE, THEN SHOW/HIDE — the highest-risk parity behaviour in Phase 7.
 *
 * MEASURED on the live page 2026-08-05: open Mfg Network, then go back to
 * Manufacturer, and `#network` still holds 26,774 characters of DOM — it is
 * merely `display:none`. Scroll position, half-filled forms, pagination and
 * grid filters therefore all SURVIVE a tab switch.
 *
 * That is `api/api.js`'s whole idiom:
 *     if ($("#network").html()) { changeCMSAppViewState("#network"); }   // show
 *     else { requirejs(["open.view.network"], ...) }                     // mount
 *
 * React's default (unmount on route change) would silently throw that state
 * away. Doing it here, once, means the 31 module tranches never have to know:
 * they render a plain component and get legacy's persistence for free.
 *
 * Implementation: `visited` accumulates every key the user has opened; every
 * visited screen stays mounted and only its `display` toggles. Screens are
 * still LAZY on first open (matching legacy's `requirejs` on first click) — the
 * bundle is not paid for until the tab is used.
 *
 * The two hosts mirror `tmpl/cms_app.tmpl`: `#menuTab` for administrative
 * screens and `#productsTabs` for product screens, with the `active` class
 * moving between them (`main.menu.js:62-68`).
 */

interface CmsScreenHostProps {
  activeKey: string;
  productId: number | string;
}

const lazyCache = new Map<string, ComponentType<CmsScreenProps>>();

const getLazyComponent = (screen: CmsScreen): ComponentType<CmsScreenProps> => {
  const cached = lazyCache.get(screen.key);
  if (cached) return cached;
  const Comp = screen.load
    ? (lazy(screen.load) as unknown as ComponentType<CmsScreenProps>)
    : ((({ screenKey }: CmsScreenProps) => (
        <CmsPendingScreen screen={CMS_SCREENS_BY_KEY[screenKey]} />
      )) as ComponentType<CmsScreenProps>);
  lazyCache.set(screen.key, Comp);
  return Comp;
};

const CmsScreenHost = ({ activeKey, productId }: CmsScreenHostProps) => {
  // Every screen the user has opened stays mounted from then on.
  const [visited, setVisited] = useState<string[]>(() => [activeKey]);
  const visitedRef = useRef(visited);
  visitedRef.current = visited;

  useEffect(() => {
    if (!visitedRef.current.includes(activeKey)) {
      setVisited((v) => [...v, activeKey]);
    }
  }, [activeKey]);

  const activeScreen = CMS_SCREENS_BY_KEY[activeKey];
  const activeSection = activeScreen?.section ?? 'admin';

  // `tmpl/cms_app.tmpl` ships an EMPTY <div> per screen up front, and the view
  // fills it in on first mount. Reproduced: every reachable screen gets its
  // slot div, but only visited ones get a child.
  const slots = useMemo(
    () => CMS_SCREENS.filter((s) => s.status !== 'dead'),
    []
  );

  const renderSlot = (screen: CmsScreen) => {
    const isVisited = visited.includes(screen.key);
    const isActive = screen.key === activeKey;
    const Comp = isVisited ? getLazyComponent(screen) : null;
    return (
      <div
        key={screen.key}
        id={screen.key}
        /* ★ T1: `cms_app.tmpl` gives `job_format`/`quote_format` an extra,
           LOAD-BEARING class before `col-xs-12` (see `slotClass` in
           cmsScreenRegistry.ts). Prefixed, not appended, to match the template's
           attribute string exactly. */
        className={screen.slotClass ? `${screen.slotClass} col-xs-12` : 'col-xs-12'}
        style={{ display: isActive ? undefined : 'none' }}
      >
        {Comp && (
          <Suspense fallback={null}>
            <Comp screenKey={screen.key} productId={productId} />
          </Suspense>
        )}
      </div>
    );
  };

  return (
    <>
      <div id="menuTab" className={activeSection === 'admin' ? 'active' : undefined}>
        {slots.filter((s) => s.section === 'admin').map(renderSlot)}
        {/* `#errorMsg` lives in #productsTabs in legacy, but api.js's error
            renderer looks it up with `$("#menuTab").find("#errorMsg")`
            (api/api.js error.renderErrorMsg) — a legacy mismatch that means the
            error block is never found. Reproduced as-is: no #errorMsg here. */}
      </div>
      <div id="productsTabs" className={activeSection === 'product' ? 'active' : undefined}>
        {slots.filter((s) => s.section === 'product').map(renderSlot)}
        {/* ★ HIDDEN, like every other `#productsTabs` child. `product.menu.js`
            opens a product screen with `$("#productsTabs").children().hide()` +
            `$("#"+tabName).show()` (:166-167, :187-188) and `api.js` hides
            `#errorMsg` again on every view change (:1438, :1449), so on the live
            page these two carry an inline `display:none` and never occupy space.
            Left visible they are not merely invisible-but-empty: `styles.css:210`
            `#productsTabs > div { height: 100% }` gives each one a FULL viewport
            height, stacking ~2× 912px below the active screen. That overflows
            `#cms-container` (`scrollHeight` 2736 vs `clientHeight` 940), and
            `#cms-container` is programmatically scrollable despite its
            `overflow-y: hidden` — so uvc's legacy-verbatim
            `e.currentTarget.scrollIntoView()` on a tab click (AccessoryScreen's
            `scrollableTabBar` mode, `uvc.js:74`) scrolled it 82px and pulled the
            breadcrumb off-screen with the whole grid. On legacy the same call is
            a no-op because nothing overflows. */}
        <div id="errorMsg" className="col-xs-12" style={{ display: 'none' }} />
        <div id="default_product_page" className="col-xs-12" style={{ display: 'none' }} />
      </div>
    </>
  );
};

export default CmsScreenHost;
